'use client';

import { c as _c } from "react/compiler-runtime";
import type { Time } from '@morpho-org/morpho-ts';
import type { PropsChainIdAddress } from '@morpho-org/ui-v6/types';
import { InfoTooltip, Select, type VariationDisplayMode, VariationLabel } from '@morpho-org/ui-v6/client';
import { Card } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import SimpleChart from '@/components/charts/components/SimpleChart';
import { formatTimeseriesData } from '@/components/charts/helpers/highcharts';
import { useHighchartsOptions } from '@/components/charts/hooks/useHighchartsOptions';
import { getTimeFrameOptionsForVaultAge, vaultV2DefaultTimeFrame } from '@/config/table';
import { splitValueAndUnit } from '@/helpers/number';
import { sharePriceQueryOptions, toHistoryRange } from './sharePrice.query';
export interface VaultV2ChartSharePriceClientProps extends PropsChainIdAddress {
  now: number;
}
const additionalOptions = {
  chart: {
    plotBackgroundColor: 'var(--muted)'
  }
};

/**
 * Chart component to display the vault V2 share price history. The header reads the default
 * range the server prefetched; a picked range loads in the background, so the chart shows its
 * own loading and error states while the card and its header stay.
 */
const VaultV2ChartSharePriceClient = t0 => {
  const $ = _c(74);
  const {
    chainId,
    address,
    now
  } = t0;
  const {
    palette
  } = useHighchartsOptions();
  const [timeFrame, setTimeFrame] = useState(vaultV2DefaultTimeFrame);
  let t1;
  if ($[0] !== address || $[1] !== chainId) {
    t1 = sharePriceQueryOptions({
      chainId,
      address,
      range: toHistoryRange(vaultV2DefaultTimeFrame)
    });
    $[0] = address;
    $[1] = chainId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useSuspenseQuery(t1);
  let t2;
  if ($[3] !== address || $[4] !== chainId || $[5] !== timeFrame) {
    t2 = sharePriceQueryOptions({
      chainId,
      address,
      range: toHistoryRange(timeFrame)
    });
    $[3] = address;
    $[4] = chainId;
    $[5] = timeFrame;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = {
      ...t2,
      placeholderData: keepPreviousData
    };
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const selected = useQuery(t3);
  const loading = selected.isPlaceholderData || selected.isPending;
  let t4;
  if ($[9] !== data.creationTimestamp || $[10] !== now) {
    t4 = getTimeFrameOptionsForVaultAge(data.creationTimestamp, now);
    $[9] = data.creationTimestamp;
    $[10] = now;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const timeFrameOptions = t4;
  const symbol = data.assetSymbol ?? "";
  let firstValue;
  let formattedData;
  let formattedValue;
  let t5;
  if ($[12] !== data.points || $[13] !== loading || $[14] !== selected.data?.points) {
    const selectedPoints = loading ? [] : selected.data?.points ?? [];
    const firstPriced = selectedPoints.findIndex(_temp);
    const pricedPoints = firstPriced === -1 ? [] : selectedPoints.slice(firstPriced);
    const points = pricedPoints.length < 2 ? [] : pricedPoints;
    formattedData = formatTimeseriesData(points.map(_temp2));
    let t6;
    if ($[19] !== data.points) {
      const latestSharePrice = data.points.at(-1)?.sharePrice ?? null;
      bb0: {
        if (latestSharePrice == null) {
          let t7;
          if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
            t7 = {
              value: null,
              unit: ""
            };
            $[21] = t7;
          } else {
            t7 = $[21];
          }
          t6 = t7;
          break bb0;
        }
        const split = splitValueAndUnit(formatters.short.default("-").digits(6).of(latestSharePrice));
        const t7 = split.value ?? null;
        const t8 = split.unit ?? "";
        let t9;
        if ($[22] !== t7 || $[23] !== t8) {
          t9 = {
            value: t7,
            unit: t8
          };
          $[22] = t7;
          $[23] = t8;
          $[24] = t9;
        } else {
          t9 = $[24];
        }
        t6 = t9;
      }
      $[19] = data.points;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    formattedValue = t6;
    firstValue = formattedData[0]?.[1];
    t5 = formattedData.at(-1)?.[1];
    $[12] = data.points;
    $[13] = loading;
    $[14] = selected.data?.points;
    $[15] = firstValue;
    $[16] = formattedData;
    $[17] = formattedValue;
    $[18] = t5;
  } else {
    firstValue = $[15];
    formattedData = $[16];
    formattedValue = $[17];
    t5 = $[18];
  }
  const lastValue = t5;
  let t6;
  if ($[25] !== firstValue || $[26] !== formattedData.length || $[27] !== lastValue) {
    t6 = formattedData.length < 2 || firstValue == null || lastValue == null ? null : {
      past: firstValue,
      present: lastValue
    };
    $[25] = firstValue;
    $[26] = formattedData.length;
    $[27] = lastValue;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const variationValues = t6;
  const [variationDisplayMode, setVariationDisplayMode] = useState("percentage");
  let t7;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setVariationDisplayMode(_temp3);
    };
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const toggleVariationDisplayMode = t7;
  let t8;
  if ($[30] !== loading || $[31] !== selected.isError) {
    t8 = {
      loading,
      error: selected.isError
    };
    $[30] = loading;
    $[31] = selected.isError;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  const statuses = t8;
  let t9;
  if ($[33] !== formattedData || $[34] !== palette.areaChartColors[0] || $[35] !== palette.colors[0]) {
    t9 = [{
      type: "areaspline" as const,
      name: "Position Unit Price",
      data: formattedData,
      color: palette.colors[0],
      lineColor: palette.colors[0],
      fillColor: palette.areaChartColors[0]
    }];
    $[33] = formattedData;
    $[34] = palette.areaChartColors[0];
    $[35] = palette.colors[0];
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  const series = t9;
  let t10;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      lineHeight: "normal"
    };
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const t11 = data.assetSymbol === null ? "" : ` (${data.assetSymbol})`;
  let t12;
  if ($[38] !== t11) {
    t12 = <span className="text-secondary-foreground text-xs" style={t10}>Position Unit Price{t11}</span>;
    $[38] = t11;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <InfoTooltip tooltipContent="This chart tracks the price of one vault position unit as an exchange rate against the deposited asset. Line growth represents accrued yield, while a sudden drop signals a realized loss or bad debt." />;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t12) {
    t14 = <div className="gap-xs flex items-center">{t12}{t13}</div>;
    $[41] = t12;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== formattedValue.value) {
    t15 = <span className="text-title-sm">{formattedValue.value}</span>;
    $[43] = formattedValue.value;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== formattedValue.unit) {
    t16 = <span className="text-title-sm text-muted-foreground">{formattedValue.unit}</span>;
    $[45] = formattedValue.unit;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t15 || $[48] !== t16) {
    t17 = <div className="flex w-[fit-content] cursor-default items-center gap-[2px]" data-testid="vault-v2-share-price-latest">{t15}{t16}</div>;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== loading || $[51] !== symbol || $[52] !== variationDisplayMode || $[53] !== variationValues) {
    t18 = <VariationLabel values={variationValues} unit={symbol} digits={6} displayMode={variationDisplayMode} onClick={toggleVariationDisplayMode} textClassName="text-xs" isLoading={loading} />;
    $[50] = loading;
    $[51] = symbol;
    $[52] = variationDisplayMode;
    $[53] = variationValues;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== t17 || $[56] !== t18) {
    t19 = <div className="gap-sm flex items-center">{t17}{t18}</div>;
    $[55] = t17;
    $[56] = t18;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t14 || $[59] !== t19) {
    t20 = <div className="gap-xs flex flex-col">{t14}{t19}</div>;
    $[58] = t14;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  let t21;
  if ($[61] !== timeFrame || $[62] !== timeFrameOptions) {
    t21 = <div className="gap-sm flex flex-wrap items-center"><Select options={timeFrameOptions} value={timeFrame} onChange={setTimeFrame} /></div>;
    $[61] = timeFrame;
    $[62] = timeFrameOptions;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t20 || $[65] !== t21) {
    t22 = <div className="gap-sm flex flex-wrap items-center justify-between">{t20}{t21}</div>;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== series || $[68] !== statuses || $[69] !== symbol) {
    t23 = <SimpleChart statuses={statuses} unit={symbol} height="224px" series={series} digits={6} additionalOptions={additionalOptions} />;
    $[67] = series;
    $[68] = statuses;
    $[69] = symbol;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] !== t22 || $[72] !== t23) {
    t24 = <Card className="gap-lg py-lg px-md md:px-lg w-[100%] flex-col" withBorder={true} data-testid="vault-v2-share-price-chart">{t22}{t23}</Card>;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  return t24;
};
export default VaultV2ChartSharePriceClient;
function _temp(t0) {
  const {
    sharePrice
  } = t0;
  return sharePrice !== null;
}
function _temp2(t0) {
  const {
    timestamp,
    sharePrice: sharePrice_0
  } = t0;
  return [timestamp, sharePrice_0];
}
function _temp3(prev) {
  return prev === "percentage" ? "amount" : "percentage";
}